import React, { useEffect, useMemo, useState } from 'react';
import { useLang } from '../../contexts/LangContext';
import { apartmentsApi, type Apartment, type ApartmentGroup } from '../../api/apartmentsApi';

interface BulkAssignApartmentsToGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  group: ApartmentGroup | null;
  buildingId: string;
}

const BulkAssignApartmentsToGroupModal: React.FC<BulkAssignApartmentsToGroupModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  group,
  buildingId,
}) => {
  const { t } = useLang();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [apartments, setApartments] = useState<Apartment[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [showUnassignedOnly, setShowUnassignedOnly] = useState(false);

  useEffect(() => {
    if (!isOpen || !group || !buildingId) {
      return;
    }

    const loadApartments = async () => {
      try {
        setLoading(true);
        setError(null);
        setSearchTerm('');
        setShowUnassignedOnly(false);
        const data = await apartmentsApi.getApartments({ buildingId });
        setApartments(data);
        const initialSelected = new Set(
          data.filter((apt) => apt.groupId === group.id).map((apt) => apt.id),
        );
        setSelectedIds(initialSelected);
      } catch (err) {
        console.error('Failed to load apartments for bulk assign:', err);
        setError(t('errorLoadingApartments'));
      } finally {
        setLoading(false);
      }
    };

    void loadApartments();
  }, [isOpen, group, buildingId, t]);

  const filteredApartments = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    return apartments.filter((apt) => {
      if (showUnassignedOnly && apt.groupId) {
        return false;
      }
      if (!normalizedSearch) {
        return true;
      }
      return (
        apt.code.toLowerCase().includes(normalizedSearch)
        || (apt.name && apt.name.toLowerCase().includes(normalizedSearch))
        || apt.ownerName.toLowerCase().includes(normalizedSearch)
      );
    });
  }, [apartments, group?.id, searchTerm, showUnassignedOnly]);

  const toggleApartment = (apartmentId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(apartmentId)) {
        next.delete(apartmentId);
      } else {
        next.add(apartmentId);
      }
      return next;
    });
  };

  const selectFiltered = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      filteredApartments.forEach((apt) => next.add(apt.id));
      return next;
    });
  };

  const selectUnassignedFiltered = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      filteredApartments
        .filter((apt) => !apt.groupId)
        .forEach((apt) => next.add(apt.id));
      return next;
    });
  };

  const clearFilteredSelection = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      filteredApartments.forEach((apt) => next.delete(apt.id));
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!group) {
      return;
    }

    try {
      setSaving(true);
      setError(null);
      await apartmentsApi.bulkAssignApartmentsToGroup(group.id, Array.from(selectedIds));
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Failed to bulk assign apartments to group:', err);
      setError(err.response?.data?.message || t('errorBulkAssignApartmentGroup'));
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || !group) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{t('bulkAssignApartmentGroup')}</h2>
            <p className="text-sm text-gray-600 mt-1">
              {t('bulkAssignApartmentGroupFor')}: <span className="font-medium text-blue-600">{group.name}</span>
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="px-6 py-4 border-b border-gray-100 space-y-3">
            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-3 py-2 rounded text-sm">
                {error}
              </div>
            )}

            <div className="flex flex-col lg:flex-row lg:items-center gap-3">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('search')}
                className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <label className="inline-flex items-center gap-2 text-sm text-gray-700 whitespace-nowrap">
                <input
                  type="checkbox"
                  checked={showUnassignedOnly}
                  onChange={(e) => setShowUnassignedOnly(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                {t('showUnassignedApartmentsOnly')}
              </label>
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={selectFiltered}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50"
              >
                {t('selectAllFiltered')}
              </button>
              <button
                type="button"
                onClick={selectUnassignedFiltered}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50"
              >
                {t('selectUnassignedFiltered')}
              </button>
              <button
                type="button"
                onClick={clearFilteredSelection}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50"
              >
                {t('clearFilteredSelection')}
              </button>
            </div>

            <div className="text-sm text-gray-600">
              {t('selectedApartmentsCount').replace('{count}', selectedIds.size.toString())}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-4">
            {loading ? (
              <div className="text-center text-gray-600 py-8">{t('loadingApartments')}</div>
            ) : filteredApartments.length === 0 ? (
              <div className="text-center text-gray-500 py-8">{t('noData')}</div>
            ) : (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                        {t('select')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('apartmentCode')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('apartmentName')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('ownerName')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('apartmentGroup')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredApartments.map((apt) => {
                      const isSelected = selectedIds.has(apt.id);
                      const currentGroupName = apt.groupId === group.id
                        ? group.name
                        : apt.group?.name;

                      return (
                        <tr
                          key={apt.id}
                          className={`hover:bg-gray-50 cursor-pointer ${isSelected ? 'bg-blue-50' : ''}`}
                          onClick={() => toggleApartment(apt.id)}
                        >
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleApartment(apt.id)}
                              onClick={(e) => e.stopPropagation()}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                          </td>
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">{apt.code}</td>
                          <td className="px-4 py-3 text-sm text-gray-700">{apt.name || '-'}</td>
                          <td className="px-4 py-3 text-sm text-gray-700">{apt.ownerName}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {currentGroupName || t('noApartmentGroup')}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
            >
              {t('cancel')}
            </button>
            <button
              type="submit"
              disabled={saving || loading}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
            >
              {saving ? t('saving') : t('save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BulkAssignApartmentsToGroupModal;
